import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Signature, Eraser } from "lucide-react";

/**
 * Componente reutilizable que muestra el texto legal + signature pad y devuelve el data URL PNG al firmar.
 * Uso:
 *   <LegalSignaturePad open={} onOpenChange={} title="…" content="…" onSign={(dataUrl) => …} />
 */
export default function LegalSignaturePad({ open, onOpenChange, title, content, onSign, kiosk = false, submitting = false }) {
    const sigRef = useRef(null);
    const [isEmpty, setIsEmpty] = useState(true);

    useEffect(() => { if (open && sigRef.current) { sigRef.current.clear(); setIsEmpty(true); } }, [open]);

    const clear = () => { sigRef.current?.clear(); setIsEmpty(true); };
    const submit = () => {
        if (!sigRef.current || sigRef.current.isEmpty()) return;
        const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
        onSign && onSign(dataUrl);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={"max-w-4xl max-h-[95vh] overflow-hidden flex flex-col " + (kiosk ? "w-full h-full" : "")} data-testid="legal-signature-pad">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto rounded-md border border-border bg-secondary/20 p-4 text-sm leading-relaxed whitespace-pre-wrap" data-testid="legal-doc-content">
                    {content}
                </div>
                <div className="mt-3">
                    <div className="text-xs uppercase-label mb-1">Firma con el dedo o el lápiz</div>
                    <div className="rounded-md border border-border bg-white" style={{ touchAction: "none" }}>
                        <SignatureCanvas
                            ref={sigRef}
                            penColor="#111"
                            canvasProps={{
                                className: "w-full h-40",
                                "data-testid": "signature-canvas",
                            }}
                            onEnd={() => setIsEmpty(false)}
                        />
                    </div>
                </div>
                <DialogFooter className="mt-3">
                    <Button variant="outline" onClick={clear} data-testid="signature-clear">
                        <Eraser size={14} className="mr-1" /> Borrar
                    </Button>
                    <Button
                        className="btn-cta"
                        disabled={isEmpty || submitting}
                        onClick={submit}
                        data-testid="signature-submit"
                    >
                        <Signature size={14} className="mr-1" /> {submitting ? "Guardando…" : "Firmar y guardar"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
